import React, { useEffect, useRef, useState } from 'react';
import { Box, Flex, Button, IconButton, Text, useBreakpointValue } from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import EventsService from '../../api/services/EventsService';
import { useTranslation } from 'react-i18next';

const PopularCategoriesSlider = ({ onCategorySelect }) => {
    const { t } = useTranslation("default");
    const [categories, setCategories] = useState([]);
    const scrollContainerRef = useRef(null);
    const [scrollState, setScrollState] = useState({ isAtStart: true, isAtEnd: false });

    const isDragging = useRef(false);
    const startX = useRef(0);
    const scrollLeft = useRef(0);
    const dragDistance = useRef(0);

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await EventsService.getCategories();
                const shuffled = [...response.data.data].sort(() => 0.5 - Math.random());
                setCategories(shuffled.slice(0, 23));
            } catch (error) {
                console.error("Failed to fetch categories:", error);
            }
        };

        fetchCategories();
    }, []);

    const checkScrollState = () => {
        if (!scrollContainerRef.current) return;
        const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
        const isAtStart = scrollLeft <= 5;
        const isAtEnd = scrollLeft + clientWidth >= scrollWidth - 5;
        setScrollState({ isAtStart, isAtEnd });
    };

    useEffect(() => {
        checkScrollState();
        window.addEventListener('resize', checkScrollState);
        return () => window.removeEventListener('resize', checkScrollState);
    }, [categories]);

    const handleMouseDown = (e) => {
        isDragging.current = true;
        dragDistance.current = 0;
        startX.current = e.pageX - scrollContainerRef.current.offsetLeft;
        scrollLeft.current = scrollContainerRef.current.scrollLeft;
        scrollContainerRef.current.style.scrollSnapType = 'none';
        scrollContainerRef.current.style.cursor = 'grabbing';
    };

    const handleMouseLeave = () => {
        isDragging.current = false;
        if (scrollContainerRef.current) {
            scrollContainerRef.current.style.scrollSnapType = 'x mandatory';
            scrollContainerRef.current.style.cursor = 'grab';
        }
    };

    const handleMouseUp = () => {
        isDragging.current = false;
        if (scrollContainerRef.current) {
            scrollContainerRef.current.style.scrollSnapType = 'x mandatory';
            scrollContainerRef.current.style.cursor = 'grab';
        }
    };

    const handleMouseMove = (e) => {
        if (!isDragging.current) return;
        e.preventDefault();
        const x = e.pageX - scrollContainerRef.current.offsetLeft;
        const walk = x - startX.current;
        dragDistance.current = Math.abs(walk);
        scrollContainerRef.current.scrollLeft = scrollLeft.current - walk * 1.5;
    };

    const scroll = (direction) => {
        if (scrollContainerRef.current) {
            const scrollAmount = scrollContainerRef.current.offsetWidth;
            scrollContainerRef.current.scrollBy({
                left: direction === 'left' ? -scrollAmount : scrollAmount,
                behavior: 'smooth'
            });
        }
    };

    const iconButtonSize = useBreakpointValue({ base: 'sm', md: 'md' });

    let maskImage = 'none';
    if (!scrollState.isAtStart && !scrollState.isAtEnd) {
        maskImage = 'linear-gradient(to right, transparent 0%, black 24px, black calc(100% - 24px), transparent 100%)';
    } else if (scrollState.isAtStart && !scrollState.isAtEnd) {
        maskImage = 'linear-gradient(to right, black calc(100% - 24px), transparent 100%)';
    } else if (!scrollState.isAtStart && scrollState.isAtEnd) {
        maskImage = 'linear-gradient(to right, transparent 0%, black 24px, black 100%)';
    }

    return (
        <Box w='100%'>
            <Text fontSize={{ base: '13px', md: '14px' }} fontWeight={700} color='brand.700' mb={{ base: 3, md: '14px' }}>
                {t('category.popular')}
            </Text>

            <Flex w='100%' align='center' gap={3}>
                {/* LEWA STRZAŁKA */}
                <IconButton
                    icon={<ChevronLeftIcon boxSize='24px' />}
                    onClick={() => scroll('left')}
                    aria-label='Scroll Left'
                    variant='outline'
                    borderRadius='full'
                    bg='white'
                    borderColor='brand.200'
                    color='brand.700'
                    _hover={{ bg: 'brand.100' }}
                    w='44px' h='44px' minW='44px'
                    display={{ base: 'none', md: 'inline-flex' }}
                    size={iconButtonSize}
                    flexShrink={0}
                />

                {/* SCROLL BOX */}
                <Box
                    ref={scrollContainerRef}
                    overflowX='auto'
                    flex='1'
                    minW={0}
                    scrollSnapType='x mandatory'
                    onMouseDown={handleMouseDown}
                    onMouseLeave={handleMouseLeave}
                    onMouseUp={handleMouseUp}
                    onMouseMove={handleMouseMove}
                    onScroll={checkScrollState}
                    sx={{
                        '&::-webkit-scrollbar': { display: 'none' },
                        scrollbarWidth: 'none',
                        cursor: 'grab',
                        maskImage: maskImage,
                        WebkitMaskImage: maskImage,
                    }}
                >
                    <Flex gap={3} py={1} w="max-content">
                        {categories.map(category => (
                            <Button
                                key={category.id}
                                variant='outline'
                                borderRadius='full'
                                size='md'
                                h='44px'
                                px={{ base: '16px', md: '18px' }}
                                fontSize={{ base: '13px', md: '14px' }}
                                fontWeight={600}
                                flexShrink={0}
                                bg='white'
                                borderColor='brand.200'
                                color='brand.700'
                                scrollSnapAlign="start"
                                onClick={() => {
                                    if (dragDistance.current > 5) return;
                                    if (onCategorySelect) onCategorySelect(category);
                                }}
                                cursor="pointer"
                                _hover={{ bg: "brand.100" }}
                            >
                                {category.name}
                            </Button>
                        ))}
                    </Flex>
                </Box>

                {/* PRAWA STRZAŁKA */}
                <IconButton
                    icon={<ChevronRightIcon boxSize='24px' />}
                    onClick={() => scroll('right')}
                    aria-label='Scroll Right'
                    variant='outline'
                    borderRadius='full'
                    bg='white'
                    borderColor='brand.200'
                    color='brand.700'
                    _hover={{ bg: 'brand.100' }}
                    w='44px' h='44px' minW='44px'
                    display={{ base: 'none', md: 'inline-flex' }}
                    size={iconButtonSize}
                    flexShrink={0}
                />
            </Flex>
        </Box>


    );
};

export default PopularCategoriesSlider;
